import { Button, Card, GitHubIcon } from "@insights/ui";
import { useParams } from "react-router";
import { PageHeader } from "../../../shared/components";
import { AppShell } from "../../../shared/layout";
import { useGitHub } from "../hooks/useGitHub";
import "../styles/github.css";

export function GitHubPage() {
  const { workspaceId } = useParams();
  const {
    query,
    repositories,
    effectiveIds,
    hasSelectionChanges,
    sync,
    saveSelection,
    disconnect,
    toggleRepository,
  } = useGitHub(workspaceId);

  return (
    <AppShell>
      <PageHeader eyebrow="Source control" title="GitHub App" />
      <Card className="setting-card">
        <div>
          <h2 className="section-card-title">
            {query.data?.status === "connected"
              ? query.data.account_login
              : "Connect GitHub"}
          </h2>
          <p>
            {query.data?.status === "connected"
              ? `Last successful sync ${query.data.last_sync_at ?? "not completed yet"} · Last webhook ${query.data.last_webhook_at ?? "not received"}`
              : "Install the separate Insights GitHub App to synchronize repositories and pull requests."}
          </p>
          {query.data?.last_error && (
            <p className="error">{query.data.last_error}</p>
          )}
          <div className="github-action-status" aria-live="polite">
            {sync.isSuccess && (
              <p className="github-action-success">
                Synchronization started. Updates may take a moment to appear.
              </p>
            )}
            {sync.error && <p className="error">{sync.error.message}</p>}
          </div>
        </div>
        {query.data?.status !== "connected" ? (
          <Button
            icon={<GitHubIcon />}
            onClick={() => (location.href = query.data!.installUrl)}
          >
            Install app
          </Button>
        ) : (
          <Button
            disabled={sync.isPending}
            onClick={() => sync.mutate()}
          >
            {sync.isPending ? "Starting sync…" : "Sync now"}
          </Button>
        )}
      </Card>
      {query.data?.status === "connected" && (
        <Card className="form-card github-repos">
          <h2 className="section-card-title">Repository selection</h2>
          <p>
            Choose which installed repositories have their pull requests,
            reviews and PR comments synchronized. Saving changes starts a sync.
          </p>
          {repositories.map((repository) => (
            <label className="row" key={repository.id}>
              <span>{repository.full_name}</span>
              <input
                type="checkbox"
                checked={effectiveIds.includes(repository.id)}
                onChange={(event) =>
                  toggleRepository(repository.id, event.target.checked)
                }
              />
            </label>
          ))}
          <div className="github-selection-actions">
            <Button
              disabled={!hasSelectionChanges || saveSelection.isPending}
              onClick={() => saveSelection.mutate()}
            >
              {saveSelection.isPending
                ? "Saving selection…"
                : "Save repository selection"}
            </Button>
            <div className="github-action-status" aria-live="polite">
              {saveSelection.isPending ? (
                <p>Saving your repository selection…</p>
              ) : hasSelectionChanges ? (
                <p>You have unsaved repository changes.</p>
              ) : saveSelection.isSuccess ? (
                <p className="github-action-success">
                  Repository selection saved. Synchronization started.
                </p>
              ) : (
                <p>Repository selection is up to date.</p>
              )}
              {saveSelection.error && (
                <p className="error">{saveSelection.error.message}</p>
              )}
            </div>
          </div>
          <button
            className="text-button danger"
            disabled={disconnect.isPending}
            onClick={() => disconnect.mutate()}
          >
            {disconnect.isPending
              ? "Disconnecting GitHub App…"
              : "Disconnect GitHub App"}
          </button>
          {disconnect.error && (
            <p className="error github-disconnect-error" aria-live="polite">
              {disconnect.error.message}
            </p>
          )}
        </Card>
      )}
    </AppShell>
  );
}
